import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, MessageCircle } from 'lucide-react';

type RegisterFormData = {
  role: string;
  password: string;
  countryCode: string;
  localPhone: string;
};

import { COUNTRY_CODES } from '../constants/phoneCountryCodes';

const LOCAL_PHONE_REGEX = /^\d{7,11}$/;
const HK_PHONE_REGEX = /^\d{8}$/;
// 常見緊急求助電話，不可作為本地電話號碼開頭
const EMERGENCY_PREFIXES = ['911', '112', '110', '119', '999', '000'];
const hasEmergencyPrefix = (localPhone: string) => EMERGENCY_PREFIXES.some(p => localPhone.startsWith(p));

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);

  const { register: authRegister } = useAuth();
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const registerSchema = useMemo(() => z.object({
    role: z.string().min(1, t('auth.roleRequired')),
    password: z.string().regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,15}$/,
      t('auth.passwordComplexity', { defaultValue: '密碼須為6-15位，並包含大楷、小楷、數字及特殊字符' })),
    countryCode: z.string().min(1, t('auth.whatsappPhoneRequired', { defaultValue: 'WhatsApp phone is required' })),
    localPhone: z.string().regex(LOCAL_PHONE_REGEX, t('auth.whatsappPhoneInvalid', { defaultValue: 'Please enter a valid mobile phone number' }))
      .refine(v => !hasEmergencyPrefix(v), { message: t('auth.phoneEmergencyPrefix', '電話號碼不可以緊急求助電話號碼開頭') }),
  }).superRefine((data, ctx) => {
    if (data.countryCode === '+852' && !HK_PHONE_REGEX.test(data.localPhone)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['localPhone'], message: t('auth.hkPhoneLengthInvalid', { defaultValue: '香港電話號碼必須為8位數字' }) });
    }
  }), [t]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      role: 'EMPLOYER',
      countryCode: '+852',
    },
  });

  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam === 'EMPLOYER' || roleParam === 'HELPER') {
      setValue('role', roleParam);
    }
  }, [searchParams, setValue]);

  const onSubmit = async (data: RegisterFormData) => {
    if (!agreedToTerms) { setTermsError(true); return; }
    setTermsError(false);
    setError('');
    setLoading(true);
    try {
      const whatsappPhone = `${data.countryCode}${data.localPhone}`;
      await authRegister(undefined, undefined, data.password, data.role as 'EMPLOYER' | 'HELPER', whatsappPhone);
      showAlert({ type: 'success', message: t('auth.registerSuccess') });
      sessionStorage.removeItem('returnUrl');
      navigate('/home');
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || t('auth.defaultError');
      setError(errorMsg);
      showAlert({ type: 'error', message: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
        <div className="flex overflow-hidden rounded-t-xl">
          <div className="w-[30%] flex items-center justify-center py-8">
            <MessageCircle className="w-10 h-10 text-primary" />
          </div>
          <div className="w-[70%] flex flex-col justify-center px-6 py-6">
            <p className="text-xl font-bold mb-1 leading-none">{t('auth.niceToMeetYou')}</p>
            <p className="text-sm text-muted-foreground">
              {t('auth.slogan1')}<br />
              {t('auth.slogan2')}
            </p>
          </div>
        </div>
        <CardContent>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-3">
              <Label>{t('auth.roleLabel')}</Label>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="flex flex-row gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="EMPLOYER" id="role-employer" />
                      <Label htmlFor="role-employer" className="font-normal">{t('auth.employerRole')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="HELPER" id="role-helper" />
                      <Label htmlFor="role-helper" className="font-normal">{t('auth.helperRole')}</Label>
                    </div>
                  </RadioGroup>
                )}
              />
              {errors.role && (
                <p className="text-sm bg-[#FFF0F0] text-[#872b57] rounded px-2 py-1">{errors.role.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="localPhone">{t('auth.whatsappPhoneLabel')}</Label>
              <div className="flex gap-2">
                <select
                  id="countryCode"
                  className="h-9 w-28 rounded-md border border-input bg-background px-2 text-sm"
                  {...register('countryCode')}
                >
                  {COUNTRY_CODES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <Input
                  id="localPhone"
                  type="tel"
                  placeholder={t('auth.phonePlaceholder', '請輸入電話號碼')}
                  className="flex-1"
                  {...register('localPhone')}
                />
              </div>
              {(errors.countryCode || errors.localPhone) && (
                <p className="text-sm bg-[#FFF0F0] text-[#872b57] rounded px-2 py-1">
                  {errors.countryCode?.message || errors.localPhone?.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.passwordLabel')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                placeholder={t('auth.passwordPlaceholder')}
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm bg-[#FFF0F0] text-[#872b57] rounded px-2 py-1">{errors.password.message}</p>
              )}
            </div>

            <div className="space-y-1">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="agreedToTerms"
                  checked={agreedToTerms}
                  onCheckedChange={(v) => { setAgreedToTerms(!!v); setTermsError(false); }}
                  className="mt-0.5"
                />
                <label htmlFor="agreedToTerms" className="text-xs text-muted-foreground cursor-pointer"
                  dangerouslySetInnerHTML={{ __html: t('auth.termsAgreementHtml', 'By continuing, you agree to our <a href="/terms.pdf" target="_blank" rel="noreferrer" class="underline hover:text-primary">Terms &amp; Conditions</a> and <a href="/privacy.pdf" target="_blank" rel="noreferrer" class="underline hover:text-primary">Privacy Policy</a>.') }}
                />
              </div>
              {termsError && (
                <p className="text-sm bg-[#FFF0F0] text-[#872b57] rounded px-2 py-1">{t('auth.termsRequired')}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? t('auth.processing') : t('auth.continueBtn')}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">{t('auth.alreadyHaveAccount')}</span>{' '}
            <Button variant="link" className="p-0 h-auto font-normal" onClick={() => navigate('/login-form')}>
              {t('auth.loginLink')}
            </Button>
          </div>
        </CardContent>
      </Card>
      </main>
    </div>

  );
};

export default RegisterPage;